import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Flex,
  GoodsCard3Column,
  type GoodsListRef,
  Spinner,
  StackScreen,
  useSafeArea,
} from '@banhala/ui-kit';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import Animated, {
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  useAddLikeGoods,
  useHandleBackPressed,
  useLikeMap,
} from 'shared/src/hooks';
import { useSyncLocalGoodsLike } from 'shared/src/hooks/useSyncLocalGoodsLike';
import { useRemoveLikeGoods } from 'shared/src/queries';
import { bridge, isNotNil, useNavigator } from 'shared/src/utils';
import type { GoodsListType } from 'shared/src/api';
import { CartIcon, ScrollToTopButton, SearchIcon } from 'shared/src/component';
import { AnalyticsScreen } from 'shared/src/context';
import { useGoodsRoute } from '../../hooks/useGoodsRoute';
import { useGoodsList } from './queries/useGoodsList';
import type { GoodsListCardItem } from './utils/convertGoodsListItemToGoodsData';
import { DeliveryFilterEmpty } from './components/DeliveryFilterEmpty';
import { GoodsListHeader } from './components/GoodsListHeader';
import { useLogViewGoodsList } from './hooks/useLogViewGoodsList';
import { useLogDisplayGoods } from './hooks/useLogDisplayGoods';
import { useLogClickGoods } from './hooks/useLogClickGoods';
import { useLogClickLikeGoods } from './hooks/useLogClickLikeGoods';
import { useLogClickDeliveryToggle } from './hooks/useLogClickDeliveryToggle';
import { useLogRefreshScreen } from './hooks/useLogRefreshScreen';

const DELIVERY_FILTER_TYPES: GoodsListType[] = [
  'similar_goods',
  'image_similar_goods',
];

const SCREEN_FALLBACK_TITLE_ITEMS: { [key: string]: string } = {
  similar_goods: '함께 클릭된 상품',
  image_similar_goods: '비슷한 상품',
};
const SCREEN_FALLBACK_TITLE = '';
const SCROLL_TO_TOP_THRESHOLD = 60;
const VIEWABILITY_CONFIG = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};

const Content = () => {
  const { params } = useGoodsRoute<'GoodsList'>();
  const { type, goodsSno, landingFrom, position } = params;

  const { bottom: safeAreaBottom } = useSafeArea();

  const { showBackButton, onPressBack } = useHandleBackPressed();

  const [isDeliveryFilterOn, setIsDeliveryFilterOn] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const listRef = useRef<GoodsListRef>(null);
  const scrollY = useSharedValue(0);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollY.value = e.nativeEvent.contentOffset.y;
    },
    [scrollY],
  );

  const handlePressScrollToTop = useCallback(() => {
    // ui-kit GoodsCard3Column → GoodsCardList → GridView 체인이 {...props} spread 로
    // ref 를 덮어쓰는 구조라 외부 ref 는 최하위 FlatList 인스턴스. scrollToIndex 는
    // 가상화된 row 가 측정 전이면 silently 실패할 수 있어 scrollToOffset 사용.
    listRef?.current?.scrollToIndex(0);
  }, []);

  const scrollToTopStyle = useAnimatedStyle(() => {
    const visible = scrollY.value > SCROLL_TO_TOP_THRESHOLD;
    return {
      opacity: withTiming(visible ? 1 : 0, { duration: 150 }),
      transform: [
        { translateY: withTiming(visible ? 0 : 60, { duration: 150 }) },
      ],
    };
  });

  const scrollToTopAnimatedProps = useAnimatedProps(() => ({
    pointerEvents: (scrollY.value > SCROLL_TO_TOP_THRESHOLD
      ? 'auto'
      : 'none') as 'auto' | 'none',
  }));
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    error,
    refetch,
    isFetching,
  } = useGoodsList({
    type,
    goodsSno,
    position,
    from: landingFrom,
    isDeliveryFilterOn,
  });

  // 에러 발생 시 토스트 + 뒤로가기 (iOS RelatedProductsVC:200-207과 동일 UX)
  useEffect(() => {
    if (!isError) return;
    const message =
      error instanceof Error && error.message
        ? error.message
        : '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    bridge.toast(message);
    onPressBack();
  }, [isError, error, onPressBack]);

  useLogViewGoodsList({
    type,
    totalCount: data?.total,
    landingFrom,
  });
  const logDisplayGoods = useLogDisplayGoods({
    type,
    listId: data?.list_id,
    listParams: data?.list_params,
  });
  const logClickGoods = useLogClickGoods({
    type,
    listId: data?.list_id,
    listParams: data?.list_params,
  });
  const logClickLikeGoods = useLogClickLikeGoods({
    type,
    listId: data?.list_id,
    listParams: data?.list_params,
  });
  const logClickDeliveryToggle = useLogClickDeliveryToggle({
    type,
    listId: data?.list_id,
  });
  const logRefreshScreen = useLogRefreshScreen();

  const handleRefresh = useCallback(() => {
    logRefreshScreen();
    setIsRefreshing(true);
    refetch().finally(() => setIsRefreshing(false));
  }, [logRefreshScreen, refetch]);

  const handleChangeDeliveryFilter = useCallback(
    (next: boolean) => {
      logClickDeliveryToggle(next);
      setIsDeliveryFilterOn(next);
    },
    [logClickDeliveryToggle],
  );

  const list = useMemo(() => data?.goods ?? [], [data?.goods]);

  const { likeMap, setLikeMap } = useLikeMap({ initialLikeMap: {} });
  useMemo(() => {
    if (!data?.goods) return;
    const initial: { [key: number]: boolean } = {};
    for (const goods of data.goods) {
      initial[goods.item.sno] = isNotNil(goods.item.like);
    }
    setLikeMap(prev => ({ ...initial, ...prev }));
  }, [data?.goods, setLikeMap]);

  const { addLikeGoods } = useAddLikeGoods({
    listParams: data?.list_params,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prev => ({ ...prev, [sno]: nextValue }));
    },
    screen: type.toUpperCase(),
  });
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  useSyncLocalGoodsLike(setLikeMap);

  const { navigator } = useNavigator();

  const handlePressItem = useCallback(
    (pressed: GoodsListCardItem, index: number) => {
      logClickGoods(pressed, index);
      navigator.moveToGoods({
        sno: pressed.item.sno,
        firstPageRendering: pressed.item.first_page_rendering
          ? {
              discount_rate: pressed.item.first_page_rendering.discount_rate,
              goods_name: pressed.item.first_page_rendering.goods_name,
              original_price: pressed.item.first_page_rendering.original_price,
              price: pressed.item.first_page_rendering.price,
              cover_image: pressed.item.first_page_rendering.cover_image,
              market_image:
                pressed.item.first_page_rendering.market_image ?? undefined,
              market_name:
                pressed.item.first_page_rendering.market_name ?? undefined,
            }
          : undefined,
      });
    },
    [logClickGoods, navigator],
  );

  const handlePressLike = useCallback(
    async (pressed: GoodsListCardItem, index: number) => {
      const sno = pressed.item.sno;
      const isLike = !likeMap[sno];
      logClickLikeGoods(pressed, index, isLike);
      if (isLike) {
        addLikeGoods(sno, { SCREEN: type.toUpperCase() });
        return;
      }
      await mutateRemoveLikeGoods({ sno });
      bridge.likeGoods({ goods_sno: sno, is_liked: false });
      setLikeMap(prev => ({ ...prev, [sno]: false }));
    },
    [
      addLikeGoods,
      likeMap,
      logClickLikeGoods,
      mutateRemoveLikeGoods,
      setLikeMap,
      type,
    ],
  );

  const showDeliveryFilter = DELIVERY_FILTER_TYPES.includes(type);
  const showDeliveryEmpty =
    showDeliveryFilter &&
    isDeliveryFilterOn &&
    list.length === 0 &&
    !isFetching;

  return (
    <AnalyticsScreen screen={type.toUpperCase()}>
      <StackScreen
        title={
          data?.title ??
          SCREEN_FALLBACK_TITLE_ITEMS[params.type] ??
          SCREEN_FALLBACK_TITLE
        }
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
        rightIcons={[<SearchIcon />, <CartIcon />]}
      >
        <GoodsListHeader
          totalCount={data?.total ?? 0}
          showDeliveryFilter={showDeliveryFilter}
          isDeliveryFilterOn={isDeliveryFilterOn}
          isLoading={isFetching && data == null}
          onChangeDeliveryFilter={handleChangeDeliveryFilter}
        />

        <GoodsCard3Column<GoodsListCardItem>
          ref={listRef}
          list={list}
          keyExtractor={key => `${key.sno}`}
          onPressItem={(item, index) => handlePressItem(item, index)}
          onPressLike={async (item, index) => handlePressLike(item, index)}
          onEndReached={() => {
            if (hasNextPage) fetchNextPage();
          }}
          onEndReachedThreshold={1}
          likeMap={likeMap}
          showSkeleton={hasNextPage}
          onViewableItemsChanged={logDisplayGoods}
          viewabilityConfig={VIEWABILITY_CONFIG}
          ListEmptyComponent={
            showDeliveryEmpty ? <DeliveryFilterEmpty /> : null
          }
          refreshing={isRefreshing}
          onRefresh={handleRefresh}
          // GoodsListProps 에 onScroll 타입이 노출되지 않지만 GridView 로 spread 되어 동작
          {...({ onScroll: handleScroll } as { onScroll: typeof handleScroll })}
        />
        <Animated.View
          style={[
            { position: 'absolute', right: 16, bottom: 54 + safeAreaBottom },
            scrollToTopStyle,
          ]}
          animatedProps={scrollToTopAnimatedProps}
        >
          <ScrollToTopButton
            testID={'ScrollToTop'}
            onPress={handlePressScrollToTop}
          />
        </Animated.View>
      </StackScreen>
    </AnalyticsScreen>
  );
};

const LoadingFallback = () => {
  const { showBackButton, onPressBack } = useHandleBackPressed();

  return (
    <StackScreen
      title={''}
      hideBackButton={!showBackButton}
      onPressBack={onPressBack}
      rightIcons={[<SearchIcon />, <CartIcon />]}
    >
      <Flex.Center flex={1}>
        <Spinner />
      </Flex.Center>
    </StackScreen>
  );
};

const GoodsListPage: React.FC = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Content />
    </Suspense>
  );
};

export default GoodsListPage;
